/**
 * Thin fetch wrapper. Sends credentials so the session cookie travels with each call,
 * and attaches the per-session CSRF token from the auth context to every mutation.
 */
export class ApiError extends Error {
  status: number; code: string; details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status; this.code = code; this.details = details;
  }
}

let csrfToken: string | null = null;
export function setCsrfToken(t: string | null) { csrfToken = t; }
export function getCsrfToken() { return csrfToken; }

// Global 401 handler — when the session has expired, any API call fires this so
// the auth layer can clear the user and send them back to the login page,
// instead of leaving them on a page where saves silently fail.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) { onUnauthorized = fn; }

export async function api<T = unknown>(path: string, opts: RequestInit = {}): Promise<T> {
  const method = (opts.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> ?? {}),
  };
  if (csrfToken && method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    headers['x-csrf-token'] = csrfToken;
  }
  const r = await fetch(path.startsWith('/') ? path : `/api/${path}`, {
    credentials: 'include',
    ...opts,
    headers,
  });
  if (!r.ok) {
    let body: unknown;
    try { body = await r.json(); } catch { body = null; }
    const b = body as { error?: string; message?: string; details?: unknown } | null;
    if (r.status === 401) onUnauthorized?.();
    throw new ApiError(r.status, b?.error ?? 'http_error', b?.message ?? r.statusText, b?.details);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

/**
 * First actionable validation message from an API error, as "field: message".
 * The server sends zod's flatten() shape ({ formErrors, fieldErrors }); an issue
 * array is accepted too. Falls back to the error message.
 */
export function describeApiError(e: unknown, fallback = 'Something went wrong'): string {
  if (!(e instanceof ApiError)) return fallback;
  const d = e.details as
    | { formErrors?: string[]; fieldErrors?: Record<string, string[] | undefined> }
    | Array<{ path?: Array<string | number>; message?: string }>
    | undefined;
  if (Array.isArray(d)) {
    const first = d[0];
    if (first?.message) return `${first.path?.join('.') || 'form'}: ${first.message}`;
  } else if (d) {
    const field = Object.entries(d.fieldErrors ?? {}).find(([, v]) => v && v.length);
    if (field) return `${field[0]}: ${field[1]![0]}`;
    if (d.formErrors?.length) return d.formErrors[0];
  }
  return e.message || fallback;
}
